import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import {
  ExitFullscreenIcon,
  FullscreenIcon,
  PauseIcon,
  PlayIcon,
  SkipBackIcon,
  SkipForwardIcon,
} from '../components/icons';
import { cx } from '../components/ui';
import { timecode } from '../lib/format';
import { planTimeline, plannedDuration, toOutput, toSource, type Placed } from '../lib/timeline';
import { useHotkeys } from '../lib/useHotkeys';
import type { Clip, EditState, Range } from '../queries/edit';

/**
 * The edit, played before it has rendered.
 *
 * MediaConvert takes a minute or two to cut a new version, and until it lands
 * the share page has only the previous one to play, which is exactly what the
 * owner just changed. This plays what they will get instead: the original
 * straight from storage, skipping the cuts, with each stitched-in clip played
 * from its own recording at its moment. It is the editor's preview in the
 * player's clothes, and it works from the same plan the render does.
 *
 * Time here is a second in the finished video. The clock, the scrub bar and
 * the arrow keys all speak that, so nothing reads as the original's length.
 * The plan (`planTimeline`) translates both ways.
 */
export function EditPreview({
  state,
  title,
  posterUrl,
}: {
  state: EditState;
  title: string;
  posterUrl: string | null;
}) {
  const duration = state.sourceDurationSec;
  const keep = useMemo<Range[]>(() => state.keep ?? [[0, duration]], [state.keep, duration]);
  const plan = useMemo(() => planTimeline(keep, state.clips, duration), [keep, state.clips, duration]);
  const total = useMemo(() => plannedDuration(plan), [plan]);
  const sources = useMemo(
    () => new Map(state.clipSources.map((source) => [source.videoId, source])),
    [state.clipSources],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const clipVideoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [activeClip, setActiveClip] = useState<Clip | null>(null);
  const [clipTime, setClipTime] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef<number | null>(null);

  // The pass so far. `crossed` is the clips already played, so one does not
  // start again when the playhead nudges past its second; `finished` is the
  // pass having run out, so the next press starts over. A seek rebuilds both.
  const crossed = useRef<Set<string>>(new Set());
  const finished = useRef(false);
  // Whether the viewer wants it running, whichever element is doing the work:
  // a clip that mounts during a paused scrub must not start on its own.
  const wantPlay = useRef(false);
  const lastTime = useRef(0);
  const clipsRef = useRef(state.clips);
  clipsRef.current = state.clips;
  // Set the moment a clip takes over, before React has rendered it, so the
  // main element's pause is not read as the viewer pressing pause.
  const activeClipRef = useRef<Clip | null>(null);
  const keepRef = useRef(keep);
  keepRef.current = keep;

  const inCut = (at: number) => !keepRef.current.some(([s, e]) => at >= s - 0.001 && at < e + 0.001);
  const nextKept = (after: number) => keepRef.current.find(([s]) => s > after)?.[0] ?? null;

  /** The first clip of this pass not yet played, at or after a second on the original. */
  const dueFrom = (at: number) =>
    [...clipsRef.current]
      .sort((a, b) => a.at - b.at)
      .find((clip) => !crossed.current.has(clip.id) && clip.at >= at - 0.001) ?? null;

  const stop = () => {
    finished.current = true;
    wantPlay.current = false;
    setPlaying(false);
  };

  const startClip = useCallback((clip: Clip, from = clip.start) => {
    crossed.current.add(clip.id);
    activeClipRef.current = clip;
    videoRef.current?.pause();
    setClipTime(from);
    setActiveClip(clip);
  }, []);

  /** The clip is over: the recording carries on, from the next kept frame. */
  const endClip = useCallback(() => {
    activeClipRef.current = null;
    setActiveClip(null);
    const video = videoRef.current;
    if (!video) return;
    const now = video.currentTime;
    // More than one clip dropped on the same second plays in turn.
    const another = dueFrom(now);
    if (another && another.at <= now + 0.001) {
      startClip(another);
      return;
    }
    if (inCut(now)) {
      const next = nextKept(now);
      if (next === null) {
        stop();
        return;
      }
      video.currentTime = next;
    }
    if (video.currentTime >= duration - 0.05) {
      stop();
      return;
    }
    if (wantPlay.current) void video.play().catch(() => {});
     
  }, [duration, startClip]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => {
      const was = lastTime.current;
      const now = video.currentTime;
      lastTime.current = now;
      setTime(now);
      if (video.paused || activeClipRef.current) return;
      // Anything dropped between the last frame and this one plays now.
      const due = clipsRef.current.find(
        (clip) => !crossed.current.has(clip.id) && clip.at > was - 0.001 && clip.at <= now + 0.001,
      );
      if (due) {
        startClip(due);
        return;
      }
      if (inCut(now)) {
        const next = nextKept(now);
        if (next !== null) video.currentTime = next;
        else {
          // Past the last kept frame. Whatever was dropped after it is the
          // outro of this recording and still has to play.
          const trailing = dueFrom(now);
          if (trailing) startClip(trailing);
          else {
            video.pause();
            stop();
          }
        }
      }
    };
    const onEnded = () => {
      const trailing = dueFrom(lastTime.current);
      if (trailing) startClip(trailing);
      else stop();
    };
    const onPlay = () => {
      wantPlay.current = true;
      finished.current = false;
      setPlaying(true);
    };
    const onPause = () => {
      // Handing over to a clip is not a pause the viewer asked for.
      if (activeClipRef.current) return;
      wantPlay.current = false;
      setPlaying(false);
    };
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', onEnded);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
     
  }, [startClip]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  // ---- where we are, in the finished video ----------------------------------
  const outTime = (() => {
    if (activeClip) {
      const piece = plan.find((one) => one.clip?.id === activeClip.id);
      if (piece) {
        const into = Math.max(0, Math.min(clipTime - activeClip.start, activeClip.end - activeClip.start));
        return piece.outStart + into;
      }
    }
    return toOutput(time, plan) ?? nearestOutput(time, plan);
  })();

  /** Go to a second in the finished video, whichever piece it is in. */
  const seekOut = (out: number) => {
    const video = videoRef.current;
    if (!video) return;
    const target = Math.max(0, Math.min(out, total));
    const piece =
      plan.find((one) => target < one.outStart + (one.end - one.start) + 0.001) ?? plan[plan.length - 1];
    if (!piece) return;
    finished.current = false;
    // Everything before this point in the finished video has been seen.
    crossed.current = new Set(
      plan.flatMap((one) => (one.clip && one.outStart < piece.outStart ? [one.clip.id] : [])),
    );
    if (piece.kind === 'clip' && piece.clip) {
      const from = piece.clip.start + (target - piece.outStart);
      const clipVideo = clipVideoRef.current;
      if (activeClipRef.current?.id === piece.clip.id && clipVideo) {
        // Within the clip already showing: its element stays mounted, so
        // it is moved by hand rather than through a fresh load.
        clipVideo.currentTime = from;
        setClipTime(from);
        return;
      }
      // The recording waits at the clip's moment, ready to resume after it.
      video.currentTime = Math.max(0, Math.min(piece.clip.at, duration));
      lastTime.current = video.currentTime;
      setTime(video.currentTime);
      startClip(piece.clip, from);
      return;
    }
    activeClipRef.current = null;
    setActiveClip(null);
    const at = Math.max(0, Math.min(toSource(target, plan, duration), duration));
    video.currentTime = at;
    lastTime.current = at;
    setTime(at);
    if (wantPlay.current) void video.play().catch(() => {});
  };

  // ---- commands -------------------------------------------------------------
  const toggle = useCallback(() => {
    const clipVideo = clipVideoRef.current;
    if (activeClipRef.current && clipVideo) {
      if (clipVideo.paused) {
        wantPlay.current = true;
        void clipVideo.play().catch(() => {});
      } else {
        wantPlay.current = false;
        clipVideo.pause();
      }
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (finished.current || video.ended) {
        // Again from the top: the pass starts over.
        crossed.current = new Set();
        finished.current = false;
        video.currentTime = keepRef.current[0]?.[0] ?? 0;
      }
      wantPlay.current = true;
      void video.play().catch(() => {});
    } else {
      wantPlay.current = false;
      video.pause();
    }
  }, []);

  const seekBy = (delta: number) => seekOut(outTime + delta);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void container.requestFullscreen().catch(() => {});
  }, []);

  useHotkeys({
    space: toggle,
    k: toggle,
    arrowleft: () => seekBy(-5),
    arrowright: () => seekBy(5),
    j: () => seekBy(-10),
    l: () => seekBy(10),
    f: toggleFullscreen,
  });

  const wake = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      if (wantPlay.current) setShowControls(false);
    }, 2600);
  }, []);

  const onScrub = (event: ChangeEvent<HTMLInputElement>) => seekOut(Number(event.target.value));

  const shown = Math.min(outTime, total);
  const progressPercent = total > 0 ? (shown / total) * 100 : 0;
  const activeSource = activeClip ? sources.get(activeClip.videoId) : undefined;

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        onMouseMove={wake}
        onTouchStart={wake}
        className="group relative overflow-hidden rounded-md border border-hairline bg-black"
      >
        <video
          ref={videoRef}
          src={state.sourceUrl}
          playsInline
          preload="metadata"
          poster={posterUrl ?? undefined}
          onClick={toggle}
          className="aspect-video w-full cursor-pointer bg-black"
        />

        {activeClip ? (
          <div className="absolute inset-0 bg-black">
            <video
              ref={clipVideoRef}
              key={activeClip.id}
              src={activeSource?.url ?? undefined}
              playsInline
              preload="auto"
              onClick={toggle}
              className="aspect-video h-full w-full cursor-pointer bg-black"
              onLoadedMetadata={(event) => {
                event.currentTarget.currentTime = clipTime;
                if (wantPlay.current) void event.currentTarget.play().catch(() => {});
              }}
              onTimeUpdate={(event) => {
                setClipTime(event.currentTarget.currentTime);
                if (event.currentTarget.currentTime >= activeClip.end) endClip();
              }}
              onEnded={endClip}
              onPlay={() => setPlaying(true)}
              // Running out is followed straight away by the recording
              // resuming; only a pause somebody asked for shows as one.
              onPause={() => {
                if (!wantPlay.current) setPlaying(false);
              }}
              // A clip that will not play is not worth stranding the
              // preview on: the recording carries on without it.
              onError={endClip}
            />
          </div>
        ) : null}

        {/* Says what this is, in the frame, so nobody mistakes it for the
            rendered file: the picture is the original at full size and the
            clips are fetched one by one, so it can stutter where the render
            will not. */}
        <span className="pointer-events-none absolute left-3 top-3 rounded bg-slate-950/80 px-2 py-1 font-mono text-micro uppercase tracking-wide text-chalk">
          Preview
        </span>

        {!playing ? (
          <button
            type="button"
            onClick={toggle}
            aria-label={`Play a preview of ${title}`}
            className="absolute inset-0 flex items-center justify-center bg-slate-950/25 transition-colors duration-120 hover:bg-slate-950/35"
          >
            <span className="brand-play flex h-16 w-16 items-center justify-center rounded-full">
              <PlayIcon size={26} />
            </span>
          </button>
        ) : null}

        <div
          className={cx(
            'absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 to-transparent px-3 pb-2.5 pt-8 transition-opacity duration-200',
            showControls || !playing ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <div className="relative mb-2 h-1.5">
            <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk/20" />
            {/* Where the clips sit in the finished video, so the bar reads
                like the composition strip in the editor. */}
            {plan.map((piece) =>
              piece.kind === 'clip' && total > 0 ? (
                <div
                  key={piece.clip?.id}
                  className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk/45"
                  style={{
                    left: `${(piece.outStart / total) * 100}%`,
                    width: `${((piece.end - piece.start) / total) * 100}%`,
                  }}
                />
              ) : null,
            )}
            <div
              className="brand-fill absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
              style={{ width: `${progressPercent}%` }}
            />
            <input
              type="range"
              min={0}
              max={total || 0}
              step={0.1}
              value={shown}
              onChange={onScrub}
              aria-label="Seek"
              className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0"
            />
          </div>

          <div className="relative flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <ControlButton onClick={toggle} label={playing ? 'Pause' : 'Play'}>
                {playing ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
              </ControlButton>
              <ControlButton onClick={() => seekBy(-5)} label="Back 5 seconds">
                <SkipBackIcon size={20} />
              </ControlButton>
              <ControlButton onClick={() => seekBy(5)} label="Forward 5 seconds">
                <SkipForwardIcon size={20} />
              </ControlButton>
            </div>

            <span
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-mono text-caption tnum text-chalk"
              data-timecode
            >
              {timecode(shown)} <span className="text-chalk-faint">/ {timecode(total)}</span>
            </span>

            <div className="flex items-center gap-1">
              {activeSource ? (
                <span className="mr-1 hidden max-w-[16rem] truncate text-caption text-chalk-dim sm:inline">
                  Clip from {activeSource.title}
                </span>
              ) : null}
              <ControlButton onClick={toggleFullscreen} label={fullscreen ? 'Exit full screen' : 'Full screen'}>
                {fullscreen ? <ExitFullscreenIcon size={20} /> : <FullscreenIcon size={20} />}
              </ControlButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A second on the original that is inside a cut, read as the finished
 * video's nearest second: the start of the piece about to play, or the end
 * when nothing is.
 */
function nearestOutput(time: number, plan: Placed[]): number {
  for (const piece of plan) {
    if (piece.kind === 'self' && time < piece.start) return piece.outStart;
  }
  return plannedDuration(plan);
}

function ControlButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded text-chalk transition-colors duration-120 hover:bg-chalk/10"
    >
      {children}
    </button>
  );
}
